"use client";

/**
 * ContactForm - the client-side form for /contact.
 *
 * Posts to `POST /api/contact` with the same Turnstile wiring used on
 * /auth/login + /auth/register. Logged-in users skip the Turnstile
 * widget (their session cookie already proves humanity, and the
 * backend mirrors this by bypassing captcha when a valid user_id
 * comes through). Success state replaces the form with a confirmation
 * card carrying the ticket reference number; "Send another" resets.
 *
 * Validation is lightweight and mostly advisory - the backend is the
 * source of truth for sanitisation, moderation, and length caps.
 */

import { useEffect, useRef, useState } from "react";
import { SELECTED } from "@/lib/ui-classes";

import TurnstileWidget from "@/components/TurnstileWidget";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useT, useLocale } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { trackEvent } from "@/lib/tracking";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Send,
} from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUBJECT_MAX = 200;
const MESSAGE_MAX = 4000;

type Category = "feedback" | "bug" | "feature" | "question" | "other";
const CATEGORIES: Category[] = [
  "feedback",
  "bug",
  "feature",
  "question",
  "other",
];
const CATEGORY_KEY: Record<Category, TranslationKey> = {
  feedback: "contact.form.category.feedback",
  bug: "contact.form.category.bug",
  feature: "contact.form.category.feature",
  question: "contact.form.category.question",
  other: "contact.form.category.other",
};

interface SuccessState {
  reference: string;
}

export default function ContactForm() {
  const t = useT();
  const { locale } = useLocale();
  const { user, loading: authLoading } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState<Category>("feedback");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  const captchaEnabled = !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const captchaRequired = captchaEnabled && !user && !authLoading;

  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  // Pre-fill email from the session when available. Only when
  // `email` is still empty so a user who typed a different address
  // on purpose (submitting for someone else) isn't overwritten on
  // re-renders.
  useEffect(() => {
    if (!email && user?.email) setEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-grow the textarea up to a reasonable cap, same pattern as
  // UserIntentInput. Keeps long feedback readable without scrolling
  // a tiny box.
  useEffect(() => {
    const el = messageRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = 280;
    const next = Math.min(el.scrollHeight, max);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [message]);

  const emailValid = EMAIL_RE.test(email.trim());
  const subjectValid = subject.trim().length > 0 && subject.length <= SUBJECT_MAX;
  const messageValid = message.trim().length > 0 && message.length <= MESSAGE_MAX;
  const canSubmit =
    emailValid &&
    subjectValid &&
    messageValid &&
    !submitting &&
    (!captchaRequired || !!turnstileToken);

  const reset = () => {
    setSubject("");
    setMessage("");
    setCategory("feedback");
    setError(null);
    setSuccess(null);
    // Leave name + email populated - the same visitor is likely to
    // send a follow-up, and re-typing those is annoying.
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Locale": locale,
        },
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: email.trim().toLowerCase(),
          category,
          subject: subject.trim(),
          message: message.trim(),
          turnstile_token: turnstileToken || undefined,
        }),
      });
      if (!res.ok) {
        // The backend returns `{ reason, message }` inside `detail`
        // for localised errors; fall back to a generic message for
        // anything else (network flake, 500, etc.).
        const body = await res.json().catch(() => null);
        const localised: string | undefined =
          body?.detail?.message || body?.detail || body?.message;
        setError(localised || t("contact.error.generic"));
        return;
      }
      const data = await res.json();
      trackEvent("contact.submit", { category });
      setSuccess({ reference: data.reference });
    } catch {
      setError(t("contact.error.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8">
        <div className="flex gap-4 items-start">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" aria-hidden />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-emerald-900">
              {t("contact.success.title")}
            </h2>
            <p className="mt-2 text-sm text-emerald-800 leading-relaxed">
              {t("contact.success.body")}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-emerald-200 rounded-lg text-xs">
              <span className="text-emerald-700">
                {t("contact.success.reference")}
              </span>
              <span className="font-mono font-semibold text-emerald-900 tracking-wider">
                {success.reference}
              </span>
            </div>
            <div className="mt-5">
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-900 hover:text-emerald-700 underline-offset-4 hover:underline"
              >
                {t("contact.success.cta")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8"
      noValidate
    >
      <h2 className="mb-6 text-lg font-bold text-gray-900">
        {t("contact.form.title")}
      </h2>

      <div className="space-y-5">
        {/* Name + Email - stacked on mobile, side-by-side on sm+ so
            the header stays compact on larger screens. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label
              htmlFor="contact-name"
              className="text-xs font-semibold text-gray-600 mb-1.5 block"
            >
              {t("contact.form.name.label")}
            </label>
            <input
              id="contact-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              maxLength={120}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label
              htmlFor="contact-email"
              className="text-xs font-semibold text-gray-600 mb-1.5 block"
            >
              {t("contact.form.email.label")}
            </label>
            <input
              id="contact-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("contact.form.email.placeholder")}
              autoComplete="email"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Category chips - radio group styled as chips, same
            treatment as /compare's mode selector. Using buttons
            rather than a native <select> so the options are visible
            at a glance; five categories fits comfortably on mobile. */}
        <div>
          <div className="text-xs font-semibold text-gray-600 mb-1.5">
            {t("contact.form.category.label")}
          </div>
          <div
            role="radiogroup"
            aria-label={t("contact.form.category.label")}
            className="flex flex-wrap gap-1.5"
          >
            {CATEGORIES.map((c) => {
              const active = category === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? SELECTED
                      : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:text-gray-900"
                  }`}
                >
                  {t(CATEGORY_KEY[c])}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="contact-subject"
            className="text-xs font-semibold text-gray-600 mb-1.5 block"
          >
            {t("contact.form.subject.label")}
          </label>
          <input
            id="contact-subject"
            type="text"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("contact.form.subject.placeholder")}
            maxLength={SUBJECT_MAX}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="contact-message"
            className="text-xs font-semibold text-gray-600 mb-1.5 block"
          >
            {t("contact.form.message.label")}
          </label>
          <textarea
            id="contact-message"
            ref={messageRef}
            required
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("contact.form.message.placeholder")}
            maxLength={MESSAGE_MAX}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none leading-6"
          />
          <div className="mt-1 flex justify-end">
            <span
              className={`text-[11px] tabular-nums ${
                message.length > MESSAGE_MAX * 0.9
                  ? "text-amber-600"
                  : "text-gray-400"
              }`}
            >
              {message.length}/{MESSAGE_MAX}
            </span>
          </div>
        </div>

        {captchaRequired && (
          <div>
            <TurnstileWidget onToken={setTurnstileToken} action="contact" />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 p-3">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            <p className="text-xs text-rose-800 leading-relaxed">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-gray-400 leading-relaxed max-w-sm">
            {t("contact.form.privacy")}
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("contact.form.submitting")}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {t("contact.form.submit")}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
